import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getServices } from '../firebase/firestore';
import { Investment } from '../types';
import { formatCurrency, getDateRange } from '../utils/helpers';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import '../styles/Premium.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faMobileAlt, faStar } from '@fortawesome/free-solid-svg-icons';

const Premium: React.FC = () => {
    const [monthlyPrice, setMonthlyPrice] = useState<number | null>(null);
    const [annualPrice, setAnnualPrice] = useState<number | null>(null);
  const { user, checkSubscription } = useAuth();
  const navigate = useNavigate();
  const investments = useState<Investment[]>([])[0];
  const [loading, setLoading] = useState(false);
  const [monthlyProfit, setMonthlyProfit] = useState(0);
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixQrCode, setPixQrCode] = useState('');

  const checkPremiumStatus = useCallback(async () => {
    await checkSubscription();
  }, [checkSubscription]);

  const calculateMonthlyProfit = useCallback(async () => {
    if (!user) return;
    try {
      const { start, end } = getDateRange('monthly');
      const services = await getServices(user.uid, start, end);
      const profit = services.reduce((sum, s) => sum + (s.value * (s.profitMargin / 100)), 0);
      setMonthlyProfit(profit);
    } catch (error) {
      console.error('Erro ao calcular lucro:', error);
      setMonthlyProfit(0);
    }
  }, [user]);

  useEffect(() => {
        // Buscar preços padrão do Firestore
        const fetchPrices = async () => {
          try {
            const adminConfigRef = doc(db, 'admin', 'config');
            const snap = await getDoc(adminConfigRef);
            if (snap.exists()) {
              const data = snap.data();
              setMonthlyPrice(data.monthlyPrice);
              setAnnualPrice(data.annualPrice);
            }
          } catch (err) {
            // Se falhar, mantém valores default
          }
        };
        fetchPrices();
    checkPremiumStatus();
    calculateMonthlyProfit();
  }, [checkPremiumStatus, calculateMonthlyProfit]);

  const handlePayment = async (paymentMethod: 'pix' | 'card') => {
    if (!user) {
      alert('Você precisa estar logado para fazer um pagamento.');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      // Garantir que estamos usando a região correta
      const functionsWithRegion = getFunctions(getApp(), 'us-central1');
      const createPaymentPreference = httpsCallable(functionsWithRegion, 'createPaymentPreference');
      console.log('Chamando função createPaymentPreference com:', { paymentMethod, plan: 'monthly' });
      const result: any = await createPaymentPreference({ paymentMethod, plan: 'monthly' });
      console.log('Resultado da função:', result);

      if (result.data.type === 'pix') {
        setPixQrCode(result.data.qrCode);
        setShowPixModal(true);
      } else if (result.data.type === 'card') {
        window.location.href = result.data.init_point;
      }
    } catch (error) {
      console.error('Erro ao iniciar pagamento:', error);
      alert('Erro ao iniciar pagamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="premium-page">
      <header className="page-header">
        <button onClick={() => navigate('/')} className="back-btn">← Voltar</button>
        <h1>Investimentos</h1>
      </header>

      <div className="profit-card">
        <h3> Lucro Mensal</h3>
        <p className="profit-amount">{formatCurrency(monthlyProfit)}</p>
      </div>

      {investments.length > 0 && (
        <div className="investments-section">
          <h3> Sugestões de Investimento (My Money IA)</h3>
          {investments.map((investment, index) => (
            <div key={index} className="investment-card">
              <div className="investment-header">
                <h4>{investment.type}</h4>
                <span className={`risk-badge ${investment.risk}`}>
                  {investment.risk === 'low' && 'Baixo Risco'}
                  {investment.risk === 'medium' && 'Médio Risco'}
                  {investment.risk === 'high' && 'Alto Risco'}
                </span>
              </div>
              <p className="investment-description">{investment.description}</p>
              <div className="investment-details">
                <div className="detail">
                  <span>Retorno Esperado:</span>
                  <strong>{investment.expectedReturn.toFixed(2)}% a.a.</strong>
                </div>
                <div className="detail">
                  <span>Valor Sugerido:</span>
                  <strong>{formatCurrency(investment.suggestedAmount)}</strong>
                </div>
              </div>
            </div>
          ))}
          <p className="disclaimer">
            <span style={{display:'inline-flex',alignItems:'center',gap:8}}><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg> Sugestões geradas por IA (My Money IA). Consulte um assessor financeiro antes de investir.</span>
          </p>
        </div>
      )}
      <div className="premium-banner" style={{ marginTop: 32 }}>
        <h2><FontAwesomeIcon icon={faStar} /> Upgrade para Premium</h2>
        <p className="price">
          {monthlyPrice !== null ? `R$ ${monthlyPrice.toFixed(2)}/mês` : 'Carregando preço mensal...'}<br />
          {annualPrice !== null ? `R$ ${annualPrice.toFixed(2)}/ano` : ''}
        </p>
        <div className="features-list">
          <div className="feature"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z"/></svg> Registro detalhado de custos</div>
          <div className="feature"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z"/></svg> Análise financeira completa</div>
          <div className="feature"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z"/></svg> Sugestões de investimentos com IA avançada</div>
          <div className="feature"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z"/></svg> Relatórios avançados</div>
          <div className="feature"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z"/></svg> Sem anúncios</div>
          <div className="feature"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z"/></svg> Suporte prioritário</div>
          <div className="feature"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z"/></svg> <b>Cálculo automático de impostos</b> <span style={{color:'#10b981'}}>(exclusivo Premium)</span></div>
          <div className="feature"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z"/></svg> <b>Tomadas de decisões financeiras com IA</b> <span style={{color:'#10b981'}}>(exclusivo Premium)</span></div>
          <div className="feature"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z"/></svg> <b>Receitas e despesas financeiras detalhadas</b> <span style={{color:'#10b981'}}>(exclusivo Premium)</span></div>
        </div>
        <div className="payment-options" style={{marginTop: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8}}>
          <div style={{display: 'flex', justifyContent: 'center', gap: 16}}>
            <button 
              className="upgrade-btn" 
              style={{fontSize: 20, padding: '14px 32px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer'}}
              onClick={() => handlePayment('card')}
              disabled={loading}
            >
              {loading ? 'Aguarde...' : <><FontAwesomeIcon icon={faCreditCard} /> Assinar com Cartão</>}
            </button>
            <button 
              className="upgrade-btn" 
              style={{fontSize: 20, padding: '14px 32px', background: '#00BFFF', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer'}}
              onClick={() => handlePayment('pix')}
              disabled={loading}
            >
              {loading ? 'Aguarde...' : <><FontAwesomeIcon icon={faMobileAlt} /> Assinar com PIX</>}
            </button>
          </div>
          <p className="payment-info" style={{fontWeight: 500, color: '#10b981', marginTop: 8}}>
            * Cancele quando quiser
          </p>
        </div>
      </div>

      {showPixModal && (
        <div className="modal-overlay" onClick={() => setShowPixModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Pagar com PIX</h2>
            <p>Escaneie o QR Code abaixo com seu aplicativo de banco:</p>
            <img src={`data:image/jpeg;base64,${pixQrCode}`} alt="PIX QR Code" style={{ maxWidth: '100%' }} />
            <p>Após o pagamento, sua assinatura será ativada automaticamente.</p>
            <button onClick={() => setShowPixModal(false)} className="cancel-btn">Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Premium;
